/**
 * NaamDarbar — Layout Wrapper (Theme-aware)
 */

import { Suspense, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import Navbar from './navbar/Navbar';
import Footer from './Footer';
import SubmitFAB from '../ui/SubmitFAB';
import PageSkeleton from '../ui/PageSkeleton';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import { claimResumeRouteFor } from '../../lib/claim';

export default function Layout() {
  const { profile, activeClaim } = useAuth();
  const { pathname } = useLocation();

  // Suppress during the signup wizard, and on the profile/claim pages where a
  // dedicated CTA already lives.
  const isInWizard = pathname.startsWith('/signup') || pathname.startsWith('/onboarding');
  const isClaimArea = pathname.startsWith('/profile') || pathname.startsWith('/claim');

  // The soft "finish your claim" nudge is dismissable (cross). Once dismissed it
  // never reappears — the dedicated CTA on the profile page is the way back.
  const [finishDismissed, setFinishDismissed] = useState(
    () => localStorage.getItem('nd:claim-banner-dismissed') === '1',
  );
  function dismissFinish() {
    localStorage.setItem('nd:claim-banner-dismissed', '1');
    setFinishDismissed(true);
  }

  // The banner reports an in-review claim. It must only appear once the claim
  // has actually been SUBMITTED — not while it's still a draft (which exists as
  // soon as an artist is picked) and not merely because the phone was verified.
  const claimStatus = activeClaim?.status;
  const isUnderReview =
    claimStatus === 'submitted' ||
    claimStatus === 'under_review' ||
    claimStatus === 'needs_more_info';

  // Show nudge banner when: intended raagi, not yet approved, claim is in review,
  // and not currently in the wizard.
  const showRaagiNudge =
    profile?.intended_role === 'raagi' &&
    profile.role !== 'raagi' &&
    profile.role !== 'admin' &&
    isUnderReview &&
    !isInWizard;

  const nudgeText =
    activeClaim?.status === 'needs_more_info'
      ? 'Admin needs more info on your Raagi claim —'
      : activeClaim?.status === 'approved'
        ? null // shouldn't reach here but guard it
        : 'Your Raagi profile claim is pending review —';

  // Softer "continue your claim" nudge: raagi-intent users only (sangat
  // accounts never see claim prompts), not yet approved, claim not in review.
  const isApprovedOrAdmin = profile?.role === 'raagi' || profile?.role === 'admin';
  const showFinishNudge =
    profile?.intended_role === 'raagi' &&
    !isApprovedOrAdmin &&
    !showRaagiNudge &&
    !isUnderReview &&
    !isInWizard &&
    !isClaimArea &&
    !finishDismissed;

  const baseFinishText =
    claimStatus === 'rejected'
      ? 'Your Raagi claim was declined —'
      : 'Continue claiming your Raagi profile —';
  // Surface the selected profile name in the banner when a claim is in progress.
  const finishText = activeClaim?.artist_name
    ? `${baseFinishText.replace(/\s—$/, '')} (${activeClaim.artist_name}) —`
    : baseFinishText;

  const finishCta =
    claimStatus === 'rejected' ? 'View status →' : 'Continue →';

  const finishRoute = claimResumeRouteFor(profile, activeClaim);

  return (
    <div className="flex min-h-screen flex-col bg-page text-tx-primary transition-colors duration-300">
      {/* Raagi onboarding nudge banner */}
      {showRaagiNudge && nudgeText && (
        <div className={cn(
          'w-full px-4 py-2 text-center text-xs font-medium',
          activeClaim?.status === 'needs_more_info'
            ? 'bg-amber-500 text-white'
            : 'bg-kesri-600 text-white',
        )}>
          {nudgeText}{' '}
          <Link to="/claim/status" className="underline underline-offset-2 hover:opacity-80">
            {activeClaim?.status === 'needs_more_info' ? 'Respond now →' : 'View status →'}
          </Link>
        </div>
      )}

      {/* Softer "finish your claim" nudge for unsubmitted claims — dismissable */}
      {showFinishNudge && (
        <div
          className="relative w-full px-9 py-2 text-center text-xs font-medium"
          style={{
            backgroundColor: 'var(--pill-badge-bg)',
            color: 'var(--pill-badge-text)',
          }}
        >
          {finishText}{' '}
          <Link to={finishRoute} className="underline underline-offset-2 hover:opacity-80">
            {finishCta}
          </Link>
          <button
            type="button"
            onClick={dismissFinish}
            aria-label="Dismiss"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-opacity hover:opacity-70"
          >
            <X size={14} />
          </button>
        </div>
      )}
      <Navbar />
      <main className="flex-1">
        <Suspense fallback={<PageSkeleton />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
      <SubmitFAB />
    </div>
  );
}
